import { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PaginationContext } from '../../components/shared/pagination/PaginationContext';
import { bookingOperationsService } from './bookingOperationsService';
import { BOOKING_OPERATIONS, OPERATION_STATUS, createCallbackConfig } from './types';

/**
 * BookingOperationsContext - Provides centralized booking operations to all components
 */
const BookingOperationsContext = createContext(null);

export const useBookingOperations = () => {
  const context = useContext(BookingOperationsContext);
  if (!context) {
    throw new Error('useBookingOperations must be used within BookingOperationsProvider');
  }
  return context;
};

/**
 * BookingOperationsProvider - Provides booking operations to child components
 */
export const BookingOperationsProvider = ({ children, mutations = {} }) => {
  const user = useSelector((store) => store.user);
  const paginationContext = useContext(PaginationContext);
  const dispatchAction = paginationContext?.dispatchAction;
  const operationStatusRef = useRef({});
  const activeOperationsRef = useRef(new Set());

  // Clean up on unmount
  useEffect(() => {
    return () => {
      bookingOperationsService.cleanup();
    };
  }, []);

  /**
   * Generic operation handler
   */
  const handleOperation = useCallback((
    operation,
    mutationFn,
    variables,
    config = {}
  ) => {
    const operationKey = `${operation}-${Date.now()}`;
    const isCallbackOnly = !mutationFn && !variables;

    // Only track as active operation if it's not callback-only
    if (!isCallbackOnly) {
      activeOperationsRef.current.add(operationKey);
    }


    try {
      // Set loading status only for actual operations
      if (!isCallbackOnly) {
        operationStatusRef.current[operation] = OPERATION_STATUS.LOADING;
      }

      // Create callback configuration
      const callbackConfig = createCallbackConfig({
        operation,
        ...config,
      });


      // Create success/error handlers
      const callbacks = {
        onSuccess: (data) => {
          if (!isCallbackOnly) {
            operationStatusRef.current[operation] = OPERATION_STATUS.SUCCESS;
            activeOperationsRef.current.delete(operationKey);
          }

          // Reset UI states if provided
          if (config.resetStates) {
            Object.entries(config.resetStates).forEach(([key, setter]) => {
              if (typeof setter === 'function') {
                setter(false);
              }
            });
          }

          // Clear selections if provided
          if (config.clearSelections) {
            config.clearSelections.forEach(setter => {
              if (typeof setter === 'function') {
                setter([]);
              }
            });
          }

          // Clear fetching state first
          if (dispatchAction) {
            dispatchAction({ type: 'fetching', payload: false });
          }

          // Dispatch pagination clear
          if (config.clearPagination !== false && dispatchAction) {
            dispatchAction({ type: 'clearAll' });
          }

          // Trigger data refresh if provided
          if (config.triggerRefresh) {
            if (typeof config.triggerRefresh === 'function') {
              config.triggerRefresh();
            } else if (dispatchAction) {
              dispatchAction({ type: 'refresh' });
            }
          }

          // Call custom success handler
          if (config.onSuccess) {
            config.onSuccess(data);
          }
        },
        onError: (error) => {
          if (!isCallbackOnly) {
            operationStatusRef.current[operation] = OPERATION_STATUS.ERROR;
            activeOperationsRef.current.delete(operationKey);
          }

          // Call custom error handler
          if (config.onError) {
            config.onError(error);
          }
        },
      };

      // Create the callback
      const callback = bookingOperationsService.createCallback(callbackConfig, callbacks);

      // Execute mutation if provided
      if (mutationFn && variables) {
        // Return a promise for direct operations
        return (async () => {
          const response = await mutationFn({ variables });
          callback(response);
          return response;
        })();
      }

      // Return callback for subscription-based operations
      // Don't track these as active operations since they're event-driven
      return callback;
    } catch (error) {
      if (!isCallbackOnly) {
        operationStatusRef.current[operation] = OPERATION_STATUS.ERROR;
        activeOperationsRef.current.delete(operationKey);
      }
      throw error;
    }
  }, [dispatchAction]);

  /**
   * Cancel booking operation
   */
  const cancelBooking = useCallback((bookingId, config = {}) => {
    return handleOperation(
      BOOKING_OPERATIONS.CANCEL,
      mutations.cancelBooking,
      { bookingId },
      config
    );
  }, [handleOperation, mutations.cancelBooking]);

  /**
   * Approve booking operation
   */
  const approveBooking = useCallback((bookingId, config = {}) => {
    return handleOperation(
      BOOKING_OPERATIONS.APPROVE,
      mutations.approveBooking,
      { bookingId },
      config
    );
  }, [handleOperation, mutations.approveBooking]);

  /**
   * Reject booking operation
   */
  const rejectBooking = useCallback((bookingId, config = {}) => {
    return handleOperation(
      BOOKING_OPERATIONS.REJECT,
      mutations.rejectBooking,
      { bookingId },
      config
    );
  }, [handleOperation, mutations.rejectBooking]);

  /**
   * Create booking callback factory
   * This is for components that need a callback for subscription-based operations
   */
  const createBookingCallback = useCallback((config = {}) => {
    // Don't await handleOperation - it returns the callback directly for subscription operations
    const callback = handleOperation(
      config.operation || BOOKING_OPERATIONS.UPDATE,
      null,
      null,
      config
    );
    // For callback-only operations, handleOperation returns the callback directly
    return callback;
  }, [handleOperation]);

  /**
   * Get operation status
   */
  const getOperationStatus = useCallback((operation) => {
    return operationStatusRef.current[operation] || OPERATION_STATUS.IDLE;
  }, []);

  /**
   * Check if any operation is loading
   */
  const isAnyOperationLoading = useCallback(() => {
    return Object.values(operationStatusRef.current).some(
      status => status === OPERATION_STATUS.LOADING
    );
  }, []);

  const value = {
    // Operations
    cancelBooking,
    approveBooking,
    rejectBooking,
    createBookingCallback,

    // Status helpers
    getOperationStatus,
    isAnyOperationLoading,

    // Direct access to service for advanced use cases
    bookingOperationsService,

    // User context
    user,
  };

  return (
    <BookingOperationsContext.Provider value={value}>
      {children}
    </BookingOperationsContext.Provider>
  );
};

export default BookingOperationsContext;
